/**
 * <sarky-rail-carousel>
 *
 * Wraps one product-rail panel's scrollable track
 * (`.sarky-product-rail__grid`) when `sections/sarky-product-rail.liquid`'s
 * `single_row_carousel` setting is on. Only loaded/defined for that mode -
 * the default (grid/reusable) rail instance never renders this element
 * and behaves exactly as before.
 *
 * No visible prev/next buttons are rendered - navigation is drag, swipe,
 * trackpad, or keyboard only.
 *
 * INTERACTION PASS 2 (this task - real drag on desktop, natural momentum
 * on mobile): an earlier pass forced every touch swipe and wheel tick to
 * move the active index by exactly one card via a hand-rolled gesture
 * engine, and had no mouse-drag support at all (touch events don't fire
 * for a mouse). Billy tested it and found the one-card cap too strict,
 * and desktop couldn't be dragged with the mouse at all. Per this task's
 * own direction, native scrolling is now the primary mechanism:
 *
 * - Touch swipe and trackpad wheel: no JS at all - the track is a plain
 *   `overflow-x: auto` element with `scroll-snap-type: x mandatory`, so
 *   the browser already scrolls it natively (with real momentum) and
 *   always rests on a card boundary. A soft swipe naturally travels
 *   about one card; a hard swipe can travel further - both are native,
 *   unconstrained scrolling, not something this file has to reimplement.
 * - Mouse drag (desktop): real click-and-hold-and-drag, added below via
 *   Pointer Events filtered to `pointerType === 'mouse'` (touch/pen
 *   pointers are left alone since native touch-scroll already handles
 *   them) - sets `scrollLeft` 1:1 with the pointer while dragging, then
 *   snaps to the nearest card via `goTo()` on release. A small
 *   move-threshold distinguishes an actual drag from a plain click, and
 *   a real drag suppresses the click that would otherwise fire on
 *   release (so links/wishlist still work normally when NOT dragging).
 * - Keyboard: unchanged - Left/Right still moves exactly one card via
 *   `goTo`/`requestStep`, which is also what a future explicit control
 *   (if one is ever added) would call.
 * - `activeIndex` is no longer only ever set by this file's own
 *   `goTo()` calls (native scroll can move the track too) - every time
 *   scrolling settles (`scrollend`, the same signal `goTo` already
 *   relied on), `activeIndex` is recomputed from the track's real
 *   scroll position via `nearestIndexForScrollLeft()`, the exact same
 *   centering formula `goTo` targets. That keeps the tracked index
 *   correct regardless of whether the last scroll was native (swipe,
 *   trackpad, drag) or JS-driven (keyboard, tab reset).
 */
class SarkyRailCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.sarky-product-rail__grid');
    this.cards = this.track ? Array.from(this.track.children) : [];
    if (!this.track || !this.cards.length) return;

    this.activeIndex = 0;
    this.isAnimating = false;
    this.pendingDelta = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.settleTimeout = null;
    // True while the track is settling from a scroll THIS element itself
    // started via goTo()'s track.scrollTo() call - see onSettled() for
    // why that distinction matters (tied clamped targets near either
    // end of the track, found via real browser testing, this task).
    this.expectingOwnScrollEnd = false;
    this.handleScrollEnd = () => {
      if (this.nativeScrollDebounce) {
        clearTimeout(this.nativeScrollDebounce);
        this.nativeScrollDebounce = null;
      }
      const ownScroll = this.expectingOwnScrollEnd;
      this.expectingOwnScrollEnd = false;
      this.onSettled(!ownScroll);
    };
    this.track.addEventListener('scrollend', this.handleScrollEnd);

    // Fallback for browsers without `scrollend` support (e.g. Safari
    // before v17) - found via real testing, this task, that a genuinely
    // native scroll (no goTo() in flight) with no `scrollend` support
    // left activeIndex stale forever after a real touch swipe, since
    // goTo()'s own settleTimeout only covers JS-driven scrolls. A plain
    // `scroll` listener is universally supported; debouncing it 150ms
    // is the standard pre-scrollend way to detect "scrolling stopped".
    // Skipped while a goTo() scroll is in flight - that scroll's own
    // scrollend/settleTimeout already owns settling it correctly.
    this.nativeScrollDebounce = null;
    this.handleScroll = () => {
      if (this.expectingOwnScrollEnd) return;
      if (this.nativeScrollDebounce) clearTimeout(this.nativeScrollDebounce);
      this.nativeScrollDebounce = setTimeout(() => {
        this.nativeScrollDebounce = null;
        if (!this.expectingOwnScrollEnd) {
          this.activeIndex = this.nearestIndexForScrollLeft(this.track.scrollLeft);
        }
      }, 150);
    };
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    this.handleKeydown = (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.requestStep(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.requestStep(-1);
      }
    };
    this.addEventListener('keydown', this.handleKeydown);

    this.isDragging = false;
    this.dragMoved = false;
    this.suppressNextClick = false;
    this.dragPointerId = null;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;

    this.handlePointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.isDragging = true;
      this.dragMoved = false;
      this.dragPointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.track.scrollLeft;
    };

    this.handlePointerMove = (event) => {
      if (!this.isDragging || event.pointerId !== this.dragPointerId) return;
      const dx = event.clientX - this.dragStartX;

      if (!this.dragMoved && Math.abs(dx) > 6) {
        this.dragMoved = true;
        this.track.setPointerCapture(this.dragPointerId);
        this.track.classList.add('is-dragging');
      }

      if (this.dragMoved) {
        event.preventDefault();
        this.track.scrollLeft = this.dragStartScrollLeft - dx;
      }
    };

    this.handlePointerUp = (event) => {
      if (!this.isDragging || event.pointerId !== this.dragPointerId) return;
      this.isDragging = false;
      this.dragPointerId = null;

      if (this.dragMoved) {
        this.track.classList.remove('is-dragging');
        this.track.releasePointerCapture(event.pointerId);
        this.suppressNextClick = true;
        this.goTo(this.nearestIndexForScrollLeft(this.track.scrollLeft));
      }
    };

    // A real drag still ends with a `click` firing on whatever element
    // is under the pointer - swallow exactly that one click so it can't
    // accidentally open a product/toggle the wishlist, without affecting
    // any other click.
    this.handleClickCapture = (event) => {
      if (this.suppressNextClick) {
        this.suppressNextClick = false;
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Defense-in-depth alongside the CSS `-webkit-user-drag: none` on
    // the card image/link itself (snippets/sarky-rail-product-card.liquid)
    // - found via real pointer testing, this task: a mouse drag starting
    // directly on a product photo fired the browser's native image
    // `dragstart` instead of ever reaching pointermove above, so the
    // track never moved. Blocking it here too covers any element inside
    // the track the CSS fix doesn't reach (e.g. a future card without
    // it applied).
    this.handleDragStart = (event) => event.preventDefault();
    this.track.addEventListener('dragstart', this.handleDragStart);

    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerUp);
    this.track.addEventListener('click', this.handleClickCapture, true);
  }

  disconnectedCallback() {
    if (!this.track) return;
    this.track.removeEventListener('scrollend', this.handleScrollEnd);
    this.track.removeEventListener('scroll', this.handleScroll);
    this.track.removeEventListener('dragstart', this.handleDragStart);
    this.track.removeEventListener('pointerdown', this.handlePointerDown);
    this.track.removeEventListener('pointermove', this.handlePointerMove);
    this.track.removeEventListener('pointerup', this.handlePointerUp);
    this.track.removeEventListener('pointercancel', this.handlePointerUp);
    this.track.removeEventListener('click', this.handleClickCapture, true);
    this.removeEventListener('keydown', this.handleKeydown);
    if (this.settleTimeout) clearTimeout(this.settleTimeout);
    if (this.nativeScrollDebounce) clearTimeout(this.nativeScrollDebounce);
  }

  /** The exact scrollLeft goTo(index) would target, clamped to the
   *  track's real [0, max] scroll range - the single source of truth
   *  both goTo() and nearestIndexForScrollLeft() build on, so they can
   *  never drift apart. Clamping here (not just in goTo) matters: near
   *  either end of the track, several cards' un-clamped centering math
   *  lands outside [0, max] and would otherwise all get silently
   *  rounded to the same real scroll position anyway - comparing
   *  un-clamped numbers there picks whichever card's raw math happens
   *  to be numerically closest, which is not necessarily the card the
   *  track is actually resting on (found via real browser testing,
   *  this task: index 0 was resyncing to index 2 at scrollLeft 0).
   *
   *  Reads the card's own computed `scroll-snap-align` instead of
   *  assuming one alignment - mobile and desktop use different values
   *  in sections/sarky-product-rail.liquid (`start` for a right-side
   *  peek on mobile, `center` for desktop's multi-card row), and a
   *  previous round's mobile-only CSS change silently changed desktop's
   *  alignment too because it lived in a rule with no media query.
   *  Deriving the formula from CSS itself, here, makes that class of
   *  drift structurally impossible instead of relying on remembering to
   *  keep two hand-written formulas in sync. */
  targetForIndex(index) {
    const card = this.cards[index];
    const maxScrollLeft = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const align = getComputedStyle(card).scrollSnapAlign;
    const raw =
      align === 'start'
        ? card.offsetLeft
        : card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2;
    return Math.max(
      0,
      Math.min(maxScrollLeft, raw)
    );
  }

  /** The card whose (clamped) scroll target is closest to the given
   *  scrollLeft - used both to decide where a manual drag should snap
   *  to and to resync activeIndex after any native (non-goTo) scroll
   *  settles. */
  nearestIndexForScrollLeft(scrollLeft) {
    let nearest = 0;
    let nearestDistance = Infinity;
    this.cards.forEach((card, index) => {
      const distance = Math.abs(this.targetForIndex(index) - scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = index;
      }
    });
    return nearest;
  }

  /** Queue a +1/-1 step (keyboard only). Collapses any rapid repeat
   *  key presses fired while still animating into at most one extra
   *  pending step, so quick repeated presses stay predictable instead
   *  of stacking. */
  requestStep(delta) {
    const nextIndex = this.activeIndex + delta;
    if (nextIndex < 0 || nextIndex > this.cards.length - 1) return;

    if (this.isAnimating) {
      this.pendingDelta = delta;
      return;
    }

    this.goTo(nextIndex);
  }

  goTo(index, { instant } = {}) {
    const clamped = Math.max(0, Math.min(this.cards.length - 1, index));
    if (!this.cards[clamped]) return;

    const target = this.targetForIndex(clamped);
    this.activeIndex = clamped;

    // Near either end of a wide desktop track, several cards' clamped
    // targets legitimately coincide (centering the 2nd-to-last card can
    // still compute to the same clamped max as the last one, once more
    // than ~2 cards already fit in view at once - and symmetrically at
    // the start). `activeIndex` was just set, above, to the one the
    // caller actually asked for - that stays authoritative for every
    // settle THIS goTo() call produces (resync: false below and in both
    // branches after it), never re-derived from scrollLeft, which can't
    // distinguish which of several tied cards was actually intended
    // (found via real browser testing, this task).
    const sameSpot = Math.abs(this.track.scrollLeft - target) < 1;
    if (sameSpot) {
      this.onSettled(false);
      return;
    }

    this.isAnimating = true;
    this.expectingOwnScrollEnd = true;
    this.track.scrollTo({
      left: target,
      behavior: instant || this.reducedMotion ? 'auto' : 'smooth',
    });

    // Fallback for browsers without `scrollend` support - matches the
    // smooth-scroll duration closely enough to avoid a visible stall.
    if (this.settleTimeout) clearTimeout(this.settleTimeout);
    this.settleTimeout = setTimeout(() => {
      this.expectingOwnScrollEnd = false;
      this.onSettled(false);
    }, 500);
  }

  /** @param {boolean} resync - Recompute activeIndex from the track's
   *  real scroll position. Only true for the `scrollend` listener when
   *  it fires WITHOUT a preceding goTo() call still in flight - i.e. a
   *  genuinely native touch/trackpad scroll goTo() never initiated.
   *  Every settle goTo() itself produces (its synchronous same-spot
   *  case, its real scrollTo() completing, or that scroll's setTimeout
   *  fallback) passes false and keeps trusting the index goTo() already
   *  set deliberately - re-deriving it from scrollLeft there can land on
   *  the wrong card when several cards' clamped targets tie near either
   *  end of the track (see goTo()'s own comment above). */
  onSettled(resync = true) {
    this.isAnimating = false;
    if (this.settleTimeout) {
      clearTimeout(this.settleTimeout);
      this.settleTimeout = null;
    }
    if (resync) {
      this.activeIndex = this.nearestIndexForScrollLeft(this.track.scrollLeft);
    }
    if (this.pendingDelta !== 0) {
      const delta = this.pendingDelta;
      this.pendingDelta = 0;
      this.requestStep(delta);
    }
  }

  /** Called by <sarky-product-rail> when this panel becomes the active
   *  tab, so re-entering a collection always starts at the first
   *  product instead of resuming a stale position. */
  reset() {
    this.pendingDelta = 0;
    this.isAnimating = false;
    this.expectingOwnScrollEnd = false;
    if (this.nativeScrollDebounce) {
      clearTimeout(this.nativeScrollDebounce);
      this.nativeScrollDebounce = null;
    }
    if (this.settleTimeout) {
      clearTimeout(this.settleTimeout);
      this.settleTimeout = null;
    }
    this.track.scrollLeft = 0;
    this.activeIndex = 0;
  }
}

if (!customElements.get('sarky-rail-carousel')) {
  customElements.define('sarky-rail-carousel', SarkyRailCarousel);
}
